import { Add } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type { ContractProductOverview } from '@vy/api-definition';
import { findIntegrationByProviderInEntities } from '@vy/services';
import { INTEGRATION_PROVIDERS, type NetSuiteProductEntity } from '@vy/types';
import { Price } from '../../../../../../../components/design/Price';
import { hasNetSuiteIntegration } from '../../../../../../../utils/integration-utils';
import { ADD_ERP_BUTTON_SX, DISABLED_TEXT_COLOR, TEXT_ELLIPSIS_SX } from './net-suite-sync-wizard-styles';
import { VyTooltip } from '../../../../../../../components/new-design/VyTooltip';

const DISABLED_ROW_TOOLTIP = 'Add ERP ID to enable syncing this product';

function wrapDisabledTooltip(content: JSX.Element, hasIntegration: boolean) {
  if (hasIntegration) {
    return content;
  }

  return (
    <VyTooltip tooltipContent={DISABLED_ROW_TOOLTIP}>
      <Box component="span">{content}</Box>
    </VyTooltip>
  );
}

export const CONTRACT_PRODUCTS_OVERVIEWS_COLUMNS_KEYS = Object.freeze({
  ID: 'id',
  NAME: 'name',
  DESCRIPTION: 'description',
  TOTAL_PRICE: 'totalPrice',
  PRODUCT_ERP: 'productErp',
} satisfies Record<string, keyof ContractProductOverview | 'productErp'>);

type CreateColumnsParams = {
  onAddErpId: (productId: string) => void;
};

export function createContractProductsOverviewsColumns({
  onAddErpId,
}: CreateColumnsParams): GridColDef<ContractProductOverview>[] {
  return [
    {
      field: CONTRACT_PRODUCTS_OVERVIEWS_COLUMNS_KEYS.NAME,
      valueGetter: (_value, row) => row.name,
      headerName: 'Product',
      flex: 1.6,
      minWidth: 220,
      renderCell: ({ row, value }) => {
        const hasIntegration = hasNetSuiteIntegration(row.integrationEntities);
        const textColor = hasIntegration ? 'inherit' : DISABLED_TEXT_COLOR;

        const content = (
          <Box component="span" sx={{ ...TEXT_ELLIPSIS_SX, color: textColor }}>
            {value}
          </Box>
        );

        return <VyTooltip tooltipContent={hasIntegration ? value : DISABLED_ROW_TOOLTIP}>{content}</VyTooltip>;
      },
    },
    {
      field: CONTRACT_PRODUCTS_OVERVIEWS_COLUMNS_KEYS.TOTAL_PRICE,
      headerName: 'Amount',
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }) => {
        const hasIntegration = hasNetSuiteIntegration(row.integrationEntities);
        const textColor = hasIntegration ? 'inherit' : DISABLED_TEXT_COLOR;
        const content = <Price style={{ color: textColor }}>{row.totalPrice}</Price>;

        return wrapDisabledTooltip(content, hasIntegration);
      },
    },
    {
      field: CONTRACT_PRODUCTS_OVERVIEWS_COLUMNS_KEYS.DESCRIPTION,
      valueGetter: (_value, row) => row.description,
      headerName: 'Description',
      flex: 1,
      minWidth: 150,
      renderCell: ({ row, value }) => {
        const hasIntegration = hasNetSuiteIntegration(row.integrationEntities);
        const textColor = hasIntegration ? 'inherit' : DISABLED_TEXT_COLOR;

        const content = (
          <Box component="span" sx={{ ...TEXT_ELLIPSIS_SX, color: textColor }}>
            {value}
          </Box>
        );

        return <VyTooltip tooltipContent={hasIntegration ? value || '' : DISABLED_ROW_TOOLTIP}>{content}</VyTooltip>;
      },
    },
    {
      field: CONTRACT_PRODUCTS_OVERVIEWS_COLUMNS_KEYS.PRODUCT_ERP,
      headerName: 'Product ERP',
      flex: 1,
      minWidth: 160,
      renderCell: ({ row }) => {
        const netSuiteEntity = findIntegrationByProviderInEntities<NetSuiteProductEntity>(
          row.integrationEntities ?? [],
          INTEGRATION_PROVIDERS.netSuite,
        );

        if (netSuiteEntity) {
          return (
            <VyTooltip tooltipContent={netSuiteEntity.metadata.name}>
              <Box component="span" sx={TEXT_ELLIPSIS_SX}>
                {netSuiteEntity.metadata.name}
              </Box>
            </VyTooltip>
          );
        }

        return (
          <VyTooltip tooltipContent="Add ERP ID to enable syncing this product">
            <Button
              variant="contained"
              disableElevation
              startIcon={<Add />}
              onClick={(event) => {
                event.stopPropagation();
                onAddErpId(row.id);
              }}
              sx={ADD_ERP_BUTTON_SX}
            >
              Add ERP ID
            </Button>
          </VyTooltip>
        );
      },
    },
  ];
}
